import { createSlice } from '@reduxjs/toolkit';

// Calculate optimal concurrency level: use all cores minus 1, minimum 1
// Parallel file jobs for the Compatibility / Loudness / Organize runs (each
// job is an ffmpeg/ffprobe process). Logical CPUs minus two — the same rule
// as the backend analysis pool (v2/analysis-pool-manager.js) — capped at 6:
// measured on a 4-core/8-thread laptop, loudness on 24 files took 26s at 3,
// 21s at 6 and 28s at 8 (ffmpeg is multi-threaded itself, so the cores are
// saturated early and 8 oversubscribes). The single source of truth — the
// settings loader (thunk/settingsThunk.js) must use this too, not its own
// number (it hard-coded 3 for years, so nobody ever ran the hardware value).
export const getOptimalConcurrency = () => {
    const hw = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 0;
    if (!hw) return 3; // hardwareConcurrency unavailable
    return Math.max(1, Math.min(6, hw - 2));
};

const initialState = {
    enableRecursive: true,
    concurrencyLevel: getOptimalConcurrency(),
    logOnlyChanges: false,
    overwriteExistingFixedFiles: false,
    // 'dark' | 'light' | 'auto' — 'auto' resolves to the OS appearance via
    // prefers-color-scheme. Default to 'dark' so brand-new users land in the
    // intended brand look instead of inheriting a light OS appearance.
    theme: 'dark',
    // How musical keys are displayed everywhere (Library, Set Planner):
    // 'camelot' → "8A" / "11B", 'musical' → "Am" / "C". Default Camelot: it
    // matches the colour wheel and makes harmonic adjacency (±1 = mixable)
    // obvious, which is the DJ use-case this serves.
    keyNotation: 'camelot',
    // Library "Date" column: false → show the year only (2022), true → the full
    // release date (2022-12-16) when the track carries one. Year-only by default.
    showFullDate: false,
    // DJ / artist name printed on exported share cards (Set Planner PNG).
    // Distinct from the account name — many users sign up with a real name
    // or just an email. '' = not set → cards render without a byline.
    // Mirrored to localStorage 'mixgoat-dj-name' immediately (appSettings
    // only flushes on beforeunload, which mobile browsers skip).
    djName: '',
    // Design v2 ("Booth" look, hero pages, shared tool header). Device-scoped,
    // mirrored to localStorage 'mixgoat-design-v2' by utils/designV2.js.
    designV2: false,
};

const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        updateSettings(state, action) {
            return { ...state, ...action.payload };
        },
    },
});

export const { updateSettings } = settingsSlice.actions;
export default settingsSlice.reducer;
